import { computed, onScopeDispose, ref, watch } from 'vue'
import { useDraftStore } from '@/stores/draftStore'
import { MessageRewriteService } from '@/features/story-conversation/MessageRewriteService'
import {
  buildChatComposerStateFromDraft,
  buildDirectorNoteFromComposerState,
  createBlankChatComposerState,
  type ChatComposerState,
} from '@/features/story-conversation/composerState'
import type { Bot } from '@/types/domain'
import type { StreamHandle } from '@/services/conversation/transport/ConversationTransport'

type RewriteMode = 'idle' | 'streaming' | 'review'

export function conversationDraftKey(owner: string, surface: string, id: string | number) {
  return `${owner}:${surface}:${id}`
}

/** Drafts and ephemeral rewrites have the same lifecycle on every story surface. */
export function useConversationComposer(options: {
  draftKey: () => string | null
  legacyDraftKey?: () => string | null
  rewriteBot: () => Bot | null
  canRewrite: () => boolean
  conversationId?: () => number | string | null
  setError: (message: string) => void
  rewriteService?: Pick<MessageRewriteService, 'start'>
}) {
  const drafts = useDraftStore()
  const service = options.rewriteService ?? new MessageRewriteService()
  const state = ref<ChatComposerState>(createBlankChatComposerState())
  const rewriteMode = ref<RewriteMode>('idle')
  const rewriteDraft = ref('')
  const rewriteBot = ref<Bot | null>(null)
  let handle: StreamHandle | null = null
  let epoch = 0
  let stopRequested = false
  let hydrating = false
  let draftRevision = 0

  const isRewriteStreaming = computed(() => rewriteMode.value === 'streaming')
  const isRewriteActive = computed(() => rewriteMode.value !== 'idle')
  const visibleState = computed(() =>
    isRewriteActive.value ? { ...state.value, message: rewriteDraft.value } : state.value
  )
  const directorNote = computed(() => buildDirectorNoteFromComposerState(state.value))
  const rewriteBotName = computed(
    () =>
      (rewriteBot.value ?? options.rewriteBot())?.name ||
      (rewriteBot.value ?? options.rewriteBot())?.model ||
      ''
  )
  const canRewrite = computed(
    () =>
      options.canRewrite() &&
      !isRewriteActive.value &&
      Boolean(options.rewriteBot()) &&
      Boolean(state.value.message.trim())
  )

  function persist() {
    const key = options.draftKey()
    if (!key || hydrating) return
    drafts.saveDraft({
      conversationId: key,
      text: state.value.message,
      director_note: directorNote.value ? { ...directorNote.value } : null,
      composer_state: { ...state.value },
    })
  }

  function clearRewrite({ abort = false } = {}) {
    epoch += 1
    if (abort) handle?.abort()
    handle = null
    stopRequested = false
    rewriteMode.value = 'idle'
    rewriteDraft.value = ''
    rewriteBot.value = null
  }

  watch(
    options.draftKey,
    (key) => {
      clearRewrite({ abort: true })
      hydrating = true
      let draft = key ? drafts.getValidDraftByConversationId(key) : null
      const legacy = options.legacyDraftKey?.()
      if (key && legacy && !draft?.text && !draft?.director_note && !draft?.composer_state) {
        const legacyDraft = drafts.getValidDraftByConversationId(legacy)
        if (legacyDraft.text || legacyDraft.director_note || legacyDraft.composer_state) {
          draft = legacyDraft
          drafts.saveDraft({ conversationId: key, ...legacyDraft })
          drafts.saveDraft({ conversationId: legacy, text: '' })
        }
      }
      const restored = buildChatComposerStateFromDraft({
        text: draft?.text ?? '',
        directorNote: draft?.director_note,
      })
      const saved = draft?.composer_state
      state.value = {
        ...restored,
        note: typeof saved?.note === 'string' ? saved.note : restored.note,
        placement:
          saved?.placement === 'prepend' || saved?.placement === 'append'
            ? saved.placement
            : restored.placement,
        expires: typeof saved?.expires === 'boolean' ? saved.expires : restored.expires,
        life:
          typeof saved?.life === 'number' && Number.isSafeInteger(saved.life) && saved.life > 0
            ? saved.life
            : restored.life,
      }
      hydrating = false
    },
    { immediate: true, flush: 'sync' }
  )
  watch(
    state,
    () => {
      draftRevision += 1
      persist()
    },
    { deep: true, flush: 'sync' }
  )

  function update(next: Partial<ChatComposerState>) {
    const normalized = { ...createBlankChatComposerState(), ...next }
    if (isRewriteActive.value) {
      rewriteDraft.value = normalized.message
      state.value = { ...normalized, message: state.value.message }
    } else state.value = normalized
  }

  function stopRewrite() {
    stopRequested = true
    handle?.abort()
  }

  async function startRewrite() {
    const bot = options.rewriteBot()
    if (!canRewrite.value || !bot) return
    const operationEpoch = ++epoch
    stopRequested = false
    rewriteBot.value = bot
    rewriteDraft.value = ''
    rewriteMode.value = 'streaming'
    options.setError('')
    try {
      const stream = await service.start({
        draft: state.value.message,
        bot,
        conversationId: options.conversationId?.() ?? null,
        onChunk: (content) => {
          if (epoch === operationEpoch) rewriteDraft.value = content
        },
        onComplete: ({ content }) => {
          if (epoch !== operationEpoch) return
          rewriteDraft.value = content
          if (content.trim()) rewriteMode.value = 'review'
          else clearRewrite()
        },
      })
      if (epoch !== operationEpoch) {
        stream.abort()
        return
      }
      handle = stream
      if (stopRequested) stream.abort()
      await stream.promise
    } catch (cause) {
      if (epoch !== operationEpoch) return
      if (rewriteDraft.value.trim()) rewriteMode.value = 'review'
      else clearRewrite()
      options.setError(
        cause instanceof Error
          ? cause.message
          : 'The message rewrite failed. Your original draft is still available.'
      )
    } finally {
      if (epoch === operationEpoch) {
        handle = null
        stopRequested = false
      }
    }
  }

  /** A failed post can restore the review as well as its original draft. */
  function takeSubmission() {
    const key = options.draftKey()
    const original = { ...state.value }
    const review = { mode: rewriteMode.value, text: rewriteDraft.value, bot: rewriteBot.value }
    const input = { text: visibleState.value.message.trim(), directorNote: directorNote.value }
    state.value = createBlankChatComposerState()
    clearRewrite()
    const clearedRevision = draftRevision
    return {
      ...input,
      restore() {
        if (
          options.draftKey() !== key ||
          draftRevision !== clearedRevision ||
          isRewriteActive.value
        )
          return
        state.value = original
        rewriteMode.value = review.mode === 'streaming' ? 'review' : review.mode
        rewriteDraft.value = review.text
        rewriteBot.value = review.bot
        persist()
      },
    }
  }

  onScopeDispose(() => clearRewrite({ abort: true }))
  return {
    state,
    visibleState,
    directorNote,
    rewriteMode,
    rewriteDraft,
    isRewriteStreaming,
    isRewriteActive,
    rewriteBotName,
    canRewrite,
    update,
    startRewrite,
    stopRewrite,
    clearRewrite,
    restoreOriginal: () => clearRewrite({ abort: true }),
    takeSubmission,
  }
}
